import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import {
  getRechargeOffer,
  addRechargeOffer as addRechargeOfferService,
  updateRechargeOffer as updateRechargeOfferService, // Fixed function name
  deleteRechargeOffer as deleteRechargeOfferService
} from "../../services/rechargeService";

import { toast } from "react-toastify";

const initialState = {
  rechargeOffer: [],
  status: "idle",
  error: null,
  totalCount: 0,
};

// Fetch all rechargeOffer
export const fetchRechargeOffer = createAsyncThunk(
  "rechargeOffer/fetchRechargeOffer",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getRechargeOffer();
      return response;
    } catch (error) {
      toast.error(error.response?.data?.message || "An Error Occurred");
      return rejectWithValue(
        error.response?.data?.message || "An Error Occurred"
      );
    }
  }
);

// Add a new RechargeOffer
export const addRechargeOffer = createAsyncThunk(
  "rechargeOffer/addRechargeOffer",
  async (rechargeOffer, { rejectWithValue }) => {
    try {
      const response = await addRechargeOfferService(rechargeOffer);
      toast.success(response.message || "RechargeOffer added successfully!");
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to add RechargeOffer";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// Update a RechargeOffer
export const updateRechargeOffer = createAsyncThunk(
  "rechargeOffer/updateRechargeOffer",
  async ({ id, rechargeOfferData }, { rejectWithValue }) => {
    try {
      const response = await updateRechargeOfferService(id, rechargeOfferData); // Now matches service function name
      toast.success(response.message || "RechargeOffer updated successfully!");
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to Update RechargeOffer";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// Delete a RechargeOffer
export const deleteRechargeOffer = createAsyncThunk(
  "rechargeOffer/deleteRechargeOffer",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteRechargeOfferService(id);
      toast.success(response.message || "RechargeOffer deleted successfully!");
      return id;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to delete RechargeOffer";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

const rechargeOfferSlice = createSlice({
  name: "rechargeOffer",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setRechargeOffer: (state, action) => {
      state.rechargeOffer = action.payload;
    },
    resetRechargeOfferState: (state) => {
      state.rechargeOffer = [];
      state.status = "idle";
      state.error = null;
      state.totalCount = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch rechargeOffer
      .addCase(fetchRechargeOffer.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchRechargeOffer.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.rechargeOffer = action.payload.data || [];
        state.totalCount =
          action.payload.totalCount || action.payload.data?.length || 0;
        state.error = null;
      })
      .addCase(fetchRechargeOffer.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        state.rechargeOffer = [];
      })

      // Add RechargeOffer
      .addCase(addRechargeOffer.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addRechargeOffer.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (action.payload.data) {
          state.rechargeOffer.unshift(action.payload.data);
          state.totalCount += 1;
        }
        state.error = null;
      })
      .addCase(addRechargeOffer.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Update RechargeOffer
      .addCase(updateRechargeOffer.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateRechargeOffer.fulfilled, (state, action) => {
        state.status = "succeeded";
        const updatedRechargeOffer = action.payload.data;

        if (updatedRechargeOffer && Array.isArray(state.rechargeOffer)) {
          const index = state.rechargeOffer.findIndex(
            (offer) => offer._id === updatedRechargeOffer._id
          );
          if (index !== -1) {
            state.rechargeOffer[index] = updatedRechargeOffer;
          }
        }
        state.error = null;
      })
      .addCase(updateRechargeOffer.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Delete RechargeOffer
      .addCase(deleteRechargeOffer.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteRechargeOffer.fulfilled, (state, action) => {
        state.status = "succeeded";
        const deletedId = action.payload;
        state.rechargeOffer = state.rechargeOffer.filter((offer) => offer._id !== deletedId);
        state.totalCount = Math.max(0, state.totalCount - 1);
        state.error = null;
      })
      .addCase(deleteRechargeOffer.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export const { clearError, resetRechargeOfferState, setRechargeOffer } = rechargeOfferSlice.actions;

// Selectors
export const selectAllRechargeOffer = (state) => state.rechargeOffer.rechargeOffer;
export const selectRechargeOfferById = (state, offerId) => 
  state.rechargeOffer.rechargeOffer.find(offer => offer._id === offerId);
export const selectRechargeOfferStatus = (state) => state.rechargeOffer.status;
export const selectRechargeOfferError = (state) => state.rechargeOffer.error;
export const selectRechargeOfferTotalCount = (state) => state.rechargeOffer.totalCount;

export default rechargeOfferSlice.reducer;